import type { Tone } from './components.tsx';

/**
 * The product's named states, and how each is drawn.
 *
 * Every state the surfaces can render is enumerated here with a word, a glyph
 * and a tone. Two consequences follow, and both are deliberate:
 *
 *   - a state cannot reach a screen as a raw enum name, because there is nowhere
 *     for one to leak through;
 *   - adding a state to the domain forces a decision about how a human reads it,
 *     at the moment the state is invented rather than at the end of the slice.
 *
 * The gate vocabulary is declared here from Slice 1, ahead of the scanner that
 * will use it. Deciding what `MESH ONLY` looks like under pressure is not work
 * that should happen the week the scanner ships (SDAD Sections 24.2, 26).
 */
export interface StatePresentation {
  readonly label: string;
  readonly tone: Tone;
  readonly glyph: string;
  /** Plain-language meaning, for tooltips and for the states index. */
  readonly meaning: string;
}

/** Organizer verification (SDAD Section 13). */
export const VERIFICATION_STATES: Readonly<Record<string, StatePresentation>> = Object.freeze({
  UNVERIFIED: {
    label: 'Not submitted',
    tone: 'neutral',
    glyph: '○',
    meaning: 'Verification has not been submitted yet.',
  },
  SUBMITTED: {
    label: 'Submitted',
    tone: 'info',
    glyph: '→',
    meaning: 'Submitted to CABS and waiting to be picked up.',
  },
  IN_REVIEW: {
    label: 'In review',
    tone: 'caution',
    glyph: '◐',
    meaning: 'A CABS reviewer is working through the evidence.',
  },
  VERIFIED: {
    label: 'Verified',
    tone: 'positive',
    glyph: '✓',
    meaning: 'Verified by CABS.',
  },
  REJECTED: {
    label: 'Rejected',
    tone: 'critical',
    glyph: '✕',
    meaning: 'Not accepted. The reason is recorded and can be resubmitted.',
  },
});

/** Event lifecycle (SDAD Section 14). */
export const EVENT_STATES: Readonly<Record<string, StatePresentation>> = Object.freeze({
  DRAFT: {
    label: 'Draft',
    tone: 'neutral',
    glyph: '○',
    meaning: 'Only your team can see this. Nothing is public.',
  },
  PUBLISHED: {
    label: 'Public',
    tone: 'positive',
    glyph: '●',
    meaning: 'Live on GateCheck. Anyone with the link can see it.',
  },
  UNPUBLISHED: {
    label: 'Taken down',
    tone: 'caution',
    glyph: '◐',
    meaning: 'Was public and is no longer. The public link now says nothing exists.',
  },
  CANCELLED: {
    label: 'Cancelled',
    tone: 'critical',
    glyph: '✕',
    meaning: 'This event will not happen.',
  },
});

/** Ticket product lifecycle. */
export const TICKET_PRODUCT_STATES: Readonly<Record<string, StatePresentation>> = Object.freeze({
  DRAFT: {
    label: 'Draft',
    tone: 'neutral',
    glyph: '○',
    meaning: 'Not shown to attendees, even when the event is public.',
  },
  PUBLISHED: {
    label: 'On the page',
    tone: 'positive',
    glyph: '●',
    meaning: 'Shown to attendees once the event itself is public.',
  },
  RETIRED: {
    label: 'Retired',
    tone: 'neutral',
    glyph: '−',
    meaning: 'No longer offered. History retained.',
  },
});

/**
 * Whether a ticket can be bought.
 *
 * `NOT_YET_ON_SALE` is the only value this release can produce: checkout arrives
 * in Slice 3, so nothing is purchasable and no surface may imply otherwise.
 */
export const SALE_STATES: Readonly<Record<string, StatePresentation>> = Object.freeze({
  NOT_YET_ON_SALE: {
    label: 'Not on sale yet',
    tone: 'neutral',
    glyph: '○',
    meaning: 'Ticket sales have not opened for this event.',
  },
  ON_SALE: { label: 'On sale', tone: 'positive', glyph: '●', meaning: 'Available to buy now.' },
  SOLD_OUT: { label: 'Sold out', tone: 'critical', glyph: '✕', meaning: 'No tickets remain.' },
  CLOSED: { label: 'Closed', tone: 'neutral', glyph: '−', meaning: 'Sales have closed.' },
});

/**
 * Attendee re-entry policy at a gate (SDAD Section 27).
 *
 * Ticket re-entry is not enforceable yet — gate readiness refuses to publish a
 * package for an event whose gates allow it — so the two non-NONE values are
 * describable but not currently choosable. Operational credentials have their
 * own policy and their own vocabulary below.
 */
export const REENTRY_POLICIES: Readonly<Record<string, StatePresentation>> = Object.freeze({
  NONE: {
    label: 'No re-entry',
    tone: 'neutral',
    glyph: '→',
    meaning: 'Once someone leaves through this gate they cannot come back in.',
  },
  SINGLE: {
    label: 'One re-entry',
    tone: 'info',
    glyph: '⟳',
    meaning: 'A guest may leave and return once.',
  },
  UNLIMITED: {
    label: 'Unlimited re-entry',
    tone: 'info',
    glyph: '∞',
    meaning: 'Guests may come and go throughout.',
  },
});

/**
 * Operational credential re-entry at a gate.
 *
 * Separate words from the attendee ones on purpose. A crew member crossing a
 * stage door repeatedly is doing their job; a guest doing it is a re-entry
 * policy question. Reading "guests may come and go" while configuring a crew
 * badge is how an organizer sets the wrong one.
 */
export const OPERATIONAL_REENTRY_POLICIES: Readonly<Record<string, StatePresentation>> =
  Object.freeze({
    // The counts are stated, not implied. "One return" reads as one crossing
    // to about half the people who see it, and an organizer who guesses wrong
    // discovers it at a door during a load-in.
    NONE: {
      label: 'One crossing',
      tone: 'neutral',
      glyph: '→',
      meaning: 'One successful crossing. No re-entry after leaving.',
    },
    SINGLE: {
      label: 'One return',
      tone: 'info',
      glyph: '⟳',
      meaning: 'Two successful crossings in total — they may leave and come back once.',
    },
    UNLIMITED: {
      label: 'As the work requires',
      tone: 'info',
      glyph: '∞',
      meaning: 'No crossing limit at this gate.',
    },
  });

/** Organization membership. */
export const MEMBERSHIP_STATES: Readonly<Record<string, StatePresentation>> = Object.freeze({
  INVITED: {
    label: 'Invited',
    tone: 'caution',
    glyph: '✉',
    meaning: 'Invitation sent. No access until it is accepted.',
  },
  ACTIVE: { label: 'Active', tone: 'positive', glyph: '✓', meaning: 'A working member.' },
  SUSPENDED: {
    label: 'Suspended',
    tone: 'critical',
    glyph: '‖',
    meaning: 'Access withheld; membership retained.',
  },
  REMOVED: {
    label: 'Removed',
    tone: 'neutral',
    glyph: '−',
    meaning: 'No longer a member. History retained.',
  },
});

/**
 * Gate connectivity (SDAD Section 24.2).
 *
 * Three states, never collapsed into "offline". Their risk differs materially:
 * under MESH ONLY the venue still has coordinated duplicate protection, and
 * under ISOLATED OFFLINE it does not. Telling an operator the same word for both
 * is the specific lie this product exists to avoid.
 */
export const GATE_CONNECTIVITY_STATES: Readonly<Record<string, StatePresentation>> = Object.freeze({
  CLOUD_AND_MESH: {
    label: 'Cloud + Mesh',
    tone: 'positive',
    glyph: '▲',
    meaning: 'Scanner reaches GateMesh and GateMesh reaches the cloud.',
  },
  MESH_ONLY: {
    label: 'Mesh only',
    tone: 'caution',
    glyph: '◆',
    meaning:
      'Internet is down. Venue-wide duplicate protection still holds across every connected gate.',
  },
  ISOLATED_OFFLINE: {
    label: 'Isolated offline',
    tone: 'critical',
    glyph: '■',
    meaning:
      'This scanner reaches neither GateMesh nor the cloud. Duplicate protection is limited to this device.',
  },
});

/**
 * Admission decisions (SDAD Section 26).
 *
 * Declared in Slice 1 so the vocabulary is settled before the scanner is built
 * in Slice 5. `ISOLATED_ACCEPT` is drawn as a caution rather than a success on
 * purpose: it was admitted, but not with full authority, and the operator must
 * be able to see that difference at a glance.
 */
export const ADMISSION_DECISIONS: Readonly<Record<string, StatePresentation>> = Object.freeze({
  ADMIT: { label: 'Admit', tone: 'positive', glyph: '✓', meaning: 'Valid. Let them through.' },
  ADMIT_REENTRY: {
    label: 'Re-entry',
    tone: 'positive',
    glyph: '⟳',
    meaning: 'Valid re-entry under this ticket’s policy.',
  },
  DENY_ALREADY_REDEEMED: {
    label: 'Already used',
    tone: 'critical',
    glyph: '✕',
    meaning: 'This entitlement was already consumed at another gate.',
  },
  DENY_REVOKED: {
    label: 'Revoked',
    tone: 'critical',
    glyph: '✕',
    meaning: 'This credential is no longer authority for admission.',
  },
  DENY_TRANSFERRED: {
    label: 'Transferred',
    tone: 'critical',
    glyph: '✕',
    meaning: 'Ownership moved. An older credential is being presented.',
  },
  DENY_WRONG_GATE: {
    label: 'Wrong gate',
    tone: 'critical',
    glyph: '⇥',
    meaning: 'Valid ticket, wrong entrance.',
  },
  DENY_WRONG_ZONE: {
    label: 'Wrong zone',
    tone: 'critical',
    glyph: '⇥',
    meaning: 'Not entitled to this zone.',
  },
  DENY_OUTSIDE_VALIDITY: {
    label: 'Outside window',
    tone: 'critical',
    glyph: '⌛',
    meaning: 'Outside this credential’s validity window.',
  },
  DENY_CREDENTIAL_INVALID: {
    label: 'Invalid',
    tone: 'critical',
    glyph: '✕',
    meaning: 'The credential did not verify.',
  },
  REVIEW_STALE_PACKAGE: {
    label: 'Stale package',
    tone: 'caution',
    glyph: '⚑',
    meaning: 'This device’s gate package is older than policy allows. Supervisor decision.',
  },
  REVIEW_IDENTITY_REQUIRED: {
    label: 'Check identity',
    tone: 'caution',
    glyph: '⚑',
    meaning: 'Policy requires an identity check before admission.',
  },
  ISOLATED_ACCEPT: {
    label: 'Isolated accept',
    tone: 'caution',
    glyph: '◆',
    meaning:
      'Admitted on this device alone. Not yet coordinated with other gates; may create a conflict.',
  },
  SUPERVISOR_OVERRIDE_ADMIT: {
    label: 'Override',
    tone: 'caution',
    glyph: '⚿',
    meaning: 'Admitted by supervisor authority. Recorded with actor and reason.',
  },
});

export function presentation(
  vocabulary: Readonly<Record<string, StatePresentation>>,
  state: string,
): StatePresentation {
  return (
    vocabulary[state] ?? {
      label: state,
      tone: 'neutral',
      glyph: '?',
      meaning: 'Unrecognised state. This client may be older than the server.',
    }
  );
}

/**
 * Order lifecycle, as a customer reads it (SDAD Section 18).
 *
 * `PAYMENT_PENDING` is drawn as a caution rather than a success, and its wording
 * never implies money has moved. The states after it exist in the vocabulary and
 * cannot be reached in this release; declaring them keeps the words stable for
 * when they can.
 */
export const ORDER_STATES: Readonly<Record<string, StatePresentation>> = Object.freeze({
  DRAFT: { label: 'Draft', tone: 'neutral', glyph: '○', meaning: 'Not yet started.' },
  QUOTED: { label: 'Quoted', tone: 'neutral', glyph: '○', meaning: 'Priced, nothing reserved.' },
  INVENTORY_HELD: {
    label: 'Tickets held',
    tone: 'info',
    glyph: '◔',
    meaning: 'Your tickets are reserved while you finish.',
  },
  PAYMENT_PENDING: {
    label: 'Awaiting payment',
    tone: 'caution',
    glyph: '◑',
    meaning: 'Reserved and unpaid. Nothing has been charged.',
  },
  EXPIRED: {
    label: 'Reservation expired',
    tone: 'neutral',
    glyph: '⌛',
    meaning: 'The hold lapsed and the tickets went back on sale.',
  },
  PAYMENT_FAILED: {
    label: 'Payment failed',
    tone: 'critical',
    glyph: '✕',
    meaning: 'The payment did not go through.',
  },
  CANCELLED: {
    label: 'Cancelled',
    tone: 'neutral',
    glyph: '−',
    meaning: 'You cancelled this. The tickets went back on sale.',
  },
  PAID_PENDING_ISSUANCE: {
    label: 'Paid',
    tone: 'positive',
    glyph: '✓',
    meaning: 'Paid. Tickets are being issued.',
  },
  /*
   * Its own words, because "Paid" would be untrue and "Free" alone would read
   * as a description of the ticket rather than of the order's state. What the
   * customer needs to know is that they are finished and nothing is owed.
   */
  ZERO_TOTAL_PENDING_ISSUANCE: {
    label: 'Confirmed',
    tone: 'positive',
    glyph: '✓',
    meaning: 'Free — nothing to pay. Tickets are being issued.',
  },
  TICKETS_ISSUED: {
    label: 'Tickets issued',
    tone: 'positive',
    glyph: '✓',
    meaning: 'Your tickets are ready.',
  },
  COMPLETED: { label: 'Complete', tone: 'positive', glyph: '✓', meaning: 'Nothing outstanding.' },
});

/**
 * Venue map version lifecycle (ADR 0036).
 *
 * These four words carry a distinction the product cannot afford to blur, and
 * two of them deliberately do not match their wire names.
 *
 * `PUBLISHED` reads as **Approved**. Everywhere else in GateCheckPro,
 * "published" means *the public can see it* — `EVENT_STATES.PUBLISHED` is drawn
 * as "Public" for exactly that reason. A published map version is not public
 * and not on sale: it is organizer-approved, immutable physical topology that
 * later event configuration is allowed to pin. Reusing the word would tell an
 * organizer they had put a room on sale by drawing it.
 *
 * `SUPERSEDED` reads as **Replaced**, and its meaning has to say the part
 * people get wrong: a replaced version is still readable, forever, because an
 * event configured against it must keep resolving to the room that was actually
 * sold. Publishing a new version never reinterprets an old one.
 */
export const VENUE_MAP_VERSION_STATES: Readonly<Record<string, StatePresentation>> = Object.freeze({
  DRAFT: {
    label: 'Draft',
    tone: 'neutral',
    glyph: '○',
    meaning: 'Being drawn. Editable, and not yet approved for anything to be configured against.',
  },
  PUBLISHED: {
    label: 'Approved',
    tone: 'positive',
    glyph: '●',
    meaning:
      'The approved shape of the room, and now permanent. Nothing here is on sale — this says what the room is, not what is being sold in it.',
  },
  SUPERSEDED: {
    label: 'Replaced',
    tone: 'info',
    glyph: '◐',
    meaning:
      'A newer approved version is now the current one. This stays readable for good, because anything already configured against it still means this room.',
  },
  WITHDRAWN: {
    label: 'Discarded',
    tone: 'neutral',
    glyph: '−',
    meaning: 'A draft that was abandoned before approval. Nothing was ever configured against it.',
  },
});

/**
 * Event seating configuration lifecycle (Slice 10B).
 *
 * A different thing from a venue map version, and the words have to keep them
 * apart. A map version is the shape of a room and belongs to the building; a
 * seating configuration is what may be sold on one night and belongs to the
 * show. An organizer looking at both on the same evening must never have to
 * work out which one a badge is describing.
 *
 * So `ACTIVE` reads as **Selling**, not "Active" and not "Approved". "Approved"
 * is already the word for a map version that has stopped moving, and reusing it
 * here would suggest that approving a room had put its seats on sale — which is
 * the exact confusion ADR 0036 exists to prevent. "Selling" says the thing that
 * is actually true and the thing an organizer needs to know before touching it.
 */
export const SEATING_CONFIGURATION_STATES: Readonly<Record<string, StatePresentation>> =
  Object.freeze({
    DRAFT: {
      label: 'Draft',
      tone: 'neutral',
      glyph: '○',
      meaning:
        'Being set up. Nothing here is on sale, and nothing is reserving a seat for anybody yet.',
    },
    ACTIVE: {
      label: 'Selling',
      tone: 'positive',
      glyph: '●',
      meaning:
        'Live for this session. Seats can be held and bought against it, so what it says can no longer change.',
    },
    SUPERSEDED: {
      label: 'Replaced',
      tone: 'info',
      glyph: '◐',
      meaning:
        'A later configuration took over for this session. This one stays readable, because seats sold against it still mean what it said.',
    },
    WITHDRAWN: {
      label: 'Discarded',
      tone: 'neutral',
      glyph: '−',
      meaning: 'A draft that was abandoned before going on sale. Nothing was ever sold against it.',
    },
  });

/**
 * What a configured seat is *for sale as*, before anybody has touched it.
 *
 * Deliberately small, and deliberately not availability. A disposition is a
 * decision an organizer made in advance and it stays true while the room fills;
 * whether a particular chair is free right now is the inventory authority's
 * answer and changes by the second. Keeping them in separate vocabularies is
 * what stops a surface from quietly treating one as the other.
 */
export const SEAT_SALE_DISPOSITIONS: Readonly<Record<string, StatePresentation>> = Object.freeze({
  SELLABLE: {
    label: 'On sale',
    tone: 'positive',
    glyph: '●',
    meaning: 'Configured to go on sale for this session.',
  },
  EXCLUDED: {
    label: 'Not for sale',
    tone: 'neutral',
    glyph: '−',
    meaning: 'Deliberately kept off sale for this session — production, sightlines, holdback.',
  },
  BLOCKED: {
    label: 'Blocked',
    tone: 'caution',
    glyph: '⊘',
    meaning:
      'Cannot go on sale at all — a physical or safety reason, not a commercial choice. Distinct from a holdback, which somebody could decide to release.',
  },
});

/**
 * What is true of one chair *right now*.
 *
 * The server's projection, and never computed here. It combines a commercial
 * decision an organizer made in advance with a claim state that changes by the
 * second, and only the server can see both — which is exactly why the browser
 * is given the answer rather than the ingredients.
 *
 * `SOLD` reads as **Sold**, not "Committed": an organizer is being told a seat
 * has gone, and the word for that is not a database transition. Kept in its own
 * register from `SEAT_SALE_DISPOSITIONS` so nothing can accidentally render a
 * standing decision as a live fact.
 */
export const SEAT_AVAILABILITY_STATES: Readonly<Record<string, StatePresentation>> = Object.freeze({
  AVAILABLE: {
    label: 'Free',
    tone: 'positive',
    glyph: '○',
    meaning: 'On sale for this session and nobody has taken it.',
  },
  HELD: {
    label: 'Being bought',
    tone: 'caution',
    glyph: '◐',
    meaning: 'Somebody has it reserved while they check out. It frees up again if they do not.',
  },
  SOLD: {
    label: 'Sold',
    tone: 'info',
    glyph: '●',
    meaning: 'Bought and paid for. It stays taken for this session.',
  },
  EXCLUDED: {
    label: 'Not for sale',
    tone: 'neutral',
    glyph: '−',
    meaning: 'Kept off sale for this session by choice.',
  },
  BLOCKED: {
    label: 'Blocked',
    tone: 'caution',
    glyph: '⊘',
    meaning: 'Cannot go on sale at all, for a physical or safety reason.',
  },
});

/**
 * The same five states, in the words a person buying a ticket would use.
 *
 * A separate vocabulary, not a separate authority. The states are the server's
 * and they do not fork here — only the sentences do, because an organizer and a
 * customer need different things from the same fact. "Being bought" is what an
 * operator watching a room wants to know; the person in front of the screen
 * wants to know they cannot have that one.
 *
 * `EXCLUDED` and `BLOCKED` are one thing to a customer. Why a chair is not for
 * sale is the organizer's business, and a buyer offered a reason they cannot
 * act on is being given noise.
 */
export const CUSTOMER_SEAT_AVAILABILITY_STATES: Readonly<Record<string, StatePresentation>> =
  Object.freeze({
    AVAILABLE: {
      label: 'Available',
      tone: 'positive',
      glyph: '○',
      meaning: 'You can choose this seat.',
    },
    HELD: {
      label: "Someone's buying this",
      tone: 'caution',
      glyph: '◐',
      meaning: 'Somebody is checking out with it. It may come back if they do not finish.',
    },
    SOLD: {
      label: 'Taken',
      tone: 'info',
      glyph: '●',
      meaning: 'Already bought.',
    },
    EXCLUDED: {
      label: 'Not for sale',
      tone: 'neutral',
      glyph: '−',
      meaning: 'Not being sold for this event.',
    },
    BLOCKED: {
      label: 'Not for sale',
      tone: 'neutral',
      glyph: '−',
      meaning: 'Not being sold for this event.',
    },
  });

/**
 * Physical facts about a chair, in the words a customer would use.
 *
 * Two facts, kept apart on purpose. A restricted view is a comment on what you
 * can see from a seat; an accessible seat is a comment on getting to it and
 * sitting in it. Merging them — a single "special seating" badge, say — would
 * tell somebody who needs step-free access that a pillar is their problem, and
 * tell somebody who minds a pillar that they should not sit there.
 *
 * Neither says anything about the person buying. GateCheckPro asks for no
 * evidence, holds no medical information and applies no eligibility rule: the
 * organizer marked the chair, and a customer chooses it the way they choose any
 * other. Wording that implied otherwise would be inventing a policy the product
 * does not have.
 */
export const SEAT_FEATURES: Readonly<Record<string, StatePresentation>> = Object.freeze({
  ACCESSIBLE: {
    label: 'Accessible seat',
    tone: 'info',
    glyph: '♿',
    meaning: 'The organizer has marked this seat as accessible.',
  },
  OBSTRUCTED_VIEW: {
    label: 'Restricted view',
    tone: 'caution',
    glyph: '◑',
    meaning: 'Part of the stage may be hidden from this seat.',
  },
});

/**
 * What kind of accessible place a chair is, in a customer's words.
 *
 * Four kinds rather than one flag, because somebody choosing between them is
 * answering a different question each time. A wheelchair space has no chair in
 * it. A transfer seat has one, with room to move beside it. The other two are
 * ordinary chairs the organizer has classified.
 *
 * Each says only what GateCheckPro actually stores, which is the
 * classification. An ambulant-access seat is not promised to be step-free and a
 * companion seat is not promised to anybody: the first would be inventing a
 * measurement nobody recorded, and the second would be inventing a reservation
 * the topology explicitly does not make. A linked companion chair may already
 * be sold, and its availability is a separate question answered elsewhere.
 *
 * What must not blur is a wheelchair space and the chair linked beside it. A
 * single "accessible" badge for both would send somebody who needs a space to a
 * chair that cannot hold one.
 *
 * As with the other seat facts, none of this asks anything of the person
 * buying. The organizer recorded what the place is; a customer reads it and
 * decides for themselves.
 */
export const SEAT_ACCESSIBILITY_PROVISIONS: Readonly<Record<string, StatePresentation>> =
  Object.freeze({
    WHEELCHAIR_SPACE: {
      label: 'Wheelchair space',
      tone: 'info',
      glyph: '♿',
      meaning: 'A space to park a wheelchair. There is no chair here.',
    },
    TRANSFER_SEAT: {
      label: 'Transfer seat',
      tone: 'info',
      glyph: '⇄',
      meaning: 'A chair with room beside it to move across from a wheelchair.',
    },
    AMBULANT_SEAT: {
      label: 'Ambulant-access seat',
      tone: 'info',
      glyph: '↧',
      meaning: 'An ordinary chair the organizer has set aside as ambulant-access seating.',
    },
    COMPANION_SEAT: {
      label: 'Linked companion seat',
      tone: 'info',
      glyph: '⇢',
      meaning:
        'An ordinary chair the organizer has linked to an accessible place. Whether it is free is a separate question.',
    },
  });

/**
 * Refund lifecycle, as an organizer and a customer read it.
 *
 * Two states carry most of the risk here, and both are about not overclaiming.
 *
 * `APPROVED` means the decision was made and the tickets are void — it does not
 * mean money has moved, and its wording says so. Between approval and
 * settlement a customer's ticket is already gone while their money has not yet
 * arrived, which is the most complained-about gap in any refund system, and the
 * only honest thing to do is name it.
 *
 * `FAILED` is drawn as a caution rather than a success and never as a
 * resolution: the payout did not happen, the tickets stay void, and somebody
 * has to try again. A surface that drew it as an ending would leave a customer
 * with neither ticket nor money and nothing to chase.
 */
export const REFUND_STATES: Readonly<Record<string, StatePresentation>> = Object.freeze({
  REQUESTED: {
    label: 'Requested',
    tone: 'info',
    glyph: '◔',
    meaning: 'Asked for. Nothing decided, and no money has moved.',
  },
  REJECTED: {
    label: 'Declined',
    tone: 'neutral',
    glyph: '−',
    meaning: 'Declined. The tickets are still valid.',
  },
  APPROVED: {
    label: 'Approved',
    tone: 'info',
    glyph: '◑',
    meaning: 'Approved and the tickets are void. The money has not gone back yet.',
  },
  PROVIDER_PENDING: {
    label: 'Sending',
    tone: 'info',
    glyph: '◕',
    meaning: 'The provider has been asked to pay it back.',
  },
  SUCCEEDED: {
    label: 'Refunded',
    tone: 'positive',
    glyph: '✓',
    meaning: 'The provider confirmed the money went back.',
  },
  FAILED: {
    label: 'Payout failed',
    tone: 'critical',
    glyph: '✕',
    meaning: 'The money did not go back. The tickets stay void and this can be tried again.',
  },
  REVIEW_REQUIRED: {
    label: 'Needs a person',
    tone: 'caution',
    glyph: '!',
    meaning: 'The provider’s answer does not reconcile. Somebody has to decide.',
  },
});

/**
 * Why a ticket is no longer valid, in the customer's own terms.
 *
 * A smaller vocabulary than the organizer's on purpose (ADR: refund contract).
 * `FRAUD` and `OPERATIONAL_ERROR` collapse into `ORGANIZER_ACTION` upstream —
 * the first is an accusation and the second an internal admission, and neither
 * is GateCheckPro's to publish on an organizer's behalf.
 *
 * None of these words says anything about money. Whether any went back is a
 * separate fact the server states separately, because a ticket can be voided
 * with nothing to refund — a free ticket has no payment to reverse — and a
 * surface that read "refunded" off the reason alone would promise a payment
 * that never existed.
 */
export const TICKET_VOID_REASONS: Readonly<Record<string, StatePresentation>> = Object.freeze({
  REFUNDED: {
    label: 'Refunded',
    tone: 'neutral',
    glyph: '−',
    meaning: 'This ticket was refunded and can no longer be used.',
  },
  EVENT_CANCELLED: {
    label: 'Event cancelled',
    tone: 'neutral',
    glyph: '−',
    meaning: 'The event was cancelled, so this ticket can no longer be used.',
  },
  ORGANIZER_ACTION: {
    label: 'Cancelled by the organizer',
    tone: 'neutral',
    glyph: '−',
    meaning: 'The organizer cancelled this ticket. It can no longer be used.',
  },
});
